import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toAppPath } from '../appPath';

/** Offset when scrolling to a section (matches sticky site navigation). */
export const sectionScrollMarginSx = {
  scrollMarginTop: { xs: 72, md: 80 },
};

const DEFAULT_ROOT_MARGIN = '-72px 0px -55% 0px';
const OBSERVER_THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 1];

/**
 * Updates the URL hash while scrolling so the active section appears in the address bar.
 * Uses IntersectionObserver and history.replaceState (no extra history entries).
 *
 * replaceState needs the full path (PUBLIC_URL + route pathname); react-router pathname omits basename.
 *
 * @param {string[]} sectionIds - DOM ids to observe, in page order (top to bottom)
 * @param {{ rootMargin?: string, clearHashOnFirstSection?: boolean }} [options]
 */
export const useScrollSpyHash = (
  sectionIds,
  { rootMargin = DEFAULT_ROOT_MARGIN, clearHashOnFirstSection = true } = {}
) => {
  const { pathname } = useLocation();
  const ratiosRef = useRef(new Map());
  const sectionIdsKey = sectionIds.join(',');

  useEffect(() => {
    ratiosRef.current.clear();

    let observer = null;
    let mutationObserver = null;
    let timeoutId = null;

    const updateHash = () => {
      const visible = [...ratiosRef.current.entries()]
        .filter(([, ratio]) => ratio > 0)
        .sort((a, b) => b[1] - a[1]);

      const activeId = visible[0]?.[0];
      if (!activeId) {
        return;
      }

      const firstSectionId = sectionIds[0];
      const nextHash =
        clearHashOnFirstSection && activeId === firstSectionId ? '' : `#${activeId}`;
      const currentHash = window.location.hash;

      if (nextHash === currentHash) {
        return;
      }

      const basePath = toAppPath(pathname);
      const url = nextHash ? `${basePath}${nextHash}` : basePath;
      window.history.replaceState(null, '', url);
    };

    const attachObserver = () => {
      const elements = sectionIds
        .map((id) => document.getElementById(id))
        .filter(Boolean);

      if (elements.length === 0) {
        return false;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            ratiosRef.current.set(entry.target.id, entry.intersectionRatio);
          });
          updateHash();
        },
        { rootMargin, threshold: OBSERVER_THRESHOLDS }
      );

      elements.forEach((el) => observer.observe(el));
      return true;
    };

    const detachObserver = () => {
      observer?.disconnect();
      observer = null;
      mutationObserver?.disconnect();
      mutationObserver = null;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    if (!attachObserver()) {
      mutationObserver = new MutationObserver(() => {
        if (attachObserver()) {
          mutationObserver.disconnect();
          mutationObserver = null;
        }
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
      timeoutId = setTimeout(detachObserver, 10000);
    }

    return detachObserver;
  }, [pathname, sectionIdsKey, rootMargin, clearHashOnFirstSection, sectionIds]);
};
